import type { AppError } from "@/shared/api";

/** Канал показу: інлайн у шіті vs глобальний toast. */
export type TimeBlockErrorSurface = "inline" | "toast";

/**
 * Типобезпечні i18n-ключі помилок блокування.
 * Доменні `journal.timeBlock.errors.*` + generic `errors.*`.
 */
export type TimeBlockErrorKey =
  | "journal.timeBlock.errors.overlapBlock"
  | "journal.timeBlock.errors.overlapBooking"
  | "journal.timeBlock.errors.invalidRange"
  | "journal.timeBlock.errors.reasonTooLong"
  | "journal.timeBlock.errors.notFound"
  | "errors.forbidden"
  | "errors.rateLimited"
  | "errors.server"
  | "errors.network"
  | "errors.unknown";

export interface MappedTimeBlockError {
  surface: TimeBlockErrorSurface;
  i18nKey: TimeBlockErrorKey;
  toastVariant: "destructive" | "info";
}

/**
 * Адаптер `AppError → MappedTimeBlockError` (патерн mapBookingError).
 * Матриця §7.1 spec:
 *
 * - 409 time_block_overlap → inline
 * - 409 time_block_overlaps_booking → inline
 * - 422 invalid_time_range → inline
 * - 422 time_block_reason_too_long → inline
 * - 404 → toast(info)
 * - решта → toast(destructive) generic
 */
export function mapTimeBlockError(e: AppError): MappedTimeBlockError {
  switch (e.kind) {
    case "conflict":
      if (e.code === "time_block_overlap") {
        return {
          surface: "inline",
          i18nKey: "journal.timeBlock.errors.overlapBlock",
          toastVariant: "destructive",
        };
      }
      if (e.code === "time_block_overlaps_booking") {
        return {
          surface: "inline",
          i18nKey: "journal.timeBlock.errors.overlapBooking",
          toastVariant: "destructive",
        };
      }
      return { surface: "toast", i18nKey: "errors.unknown", toastVariant: "destructive" };
    case "validation":
      if (e.code === "invalid_time_range") {
        return {
          surface: "inline",
          i18nKey: "journal.timeBlock.errors.invalidRange",
          toastVariant: "destructive",
        };
      }
      if (e.code === "time_block_reason_too_long") {
        return {
          surface: "inline",
          i18nKey: "journal.timeBlock.errors.reasonTooLong",
          toastVariant: "destructive",
        };
      }
      return { surface: "toast", i18nKey: "errors.unknown", toastVariant: "destructive" };
    case "notFound":
      return {
        surface: "toast",
        i18nKey: "journal.timeBlock.errors.notFound",
        toastVariant: "info",
      };
    case "forbidden":
      return { surface: "toast", i18nKey: "errors.forbidden", toastVariant: "destructive" };
    case "rateLimited":
      return { surface: "toast", i18nKey: "errors.rateLimited", toastVariant: "destructive" };
    case "network":
      return { surface: "toast", i18nKey: "errors.network", toastVariant: "destructive" };
    case "server":
      return { surface: "toast", i18nKey: "errors.server", toastVariant: "destructive" };
    default:
      return { surface: "toast", i18nKey: "errors.unknown", toastVariant: "destructive" };
  }
}
